import React from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { ColumnsIcon, ListBulletIcon } from '@radix-ui/react-icons';
import { SegmentedControl } from '@producer/ui-kit';

import { SaveTaskDto, Task, TaskLevel } from '../../../app/entities';
import { ToolBar } from '../../../components/toolbar/toolbar';
import { taskGroupingStore } from '../../../core/stores/task-store';
import { GroupingDropdown } from './grouping-dropdown';
import { SpaceTasksFilters } from './space-tasks-filters/space-tasks-filters';
import { ProjectTasksFilters } from './project-tasks-filters/project-tasks-filters';
import { TaskList } from './tasks-list';
import { TaskBoard } from './board/task-board';

type TasksViewProps = {
  tasks: Task[];
  taskLevel: TaskLevel;
  onClearFilters?: () => void;
  onSelectTask?: (taskId: string) => void;
  defaultValues?: Partial<SaveTaskDto>;
};

/**
 * Wraps the tasks toolbar (filters + grouping + List/Kanban toggle) and renders
 * either the list or the kanban board based on `taskGroupingStore.viewType`.
 * Used by the project and space tasks routes.
 */
export const TasksView: React.FC<TasksViewProps> = observer(
  ({ tasks, taskLevel, onClearFilters, onSelectTask, defaultValues }) => {
    const viewType = taskGroupingStore.viewType;

    return (
      <>
        <ToolBar>
          {taskLevel === 'space' ? <SpaceTasksFilters /> : <ProjectTasksFilters />}

          <div />

          <Right>
            <SegmentedControl>
              <span
                data-active={viewType === 'list'}
                onClick={() => taskGroupingStore.setViewType('list')}
              >
                <ListBulletIcon />
              </span>

              <span
                data-active={viewType === 'kanban'}
                onClick={() => taskGroupingStore.setViewType('kanban')}
              >
                <ColumnsIcon />
              </span>
            </SegmentedControl>

            <GroupingDropdown taskLevel={taskLevel} />
          </Right>
        </ToolBar>

        {viewType === 'kanban' ? (
          <TaskBoard
            key={taskGroupingStore.groupingKey}
            tasks={tasks}
            taskLevel={taskLevel}
            onSelectTask={onSelectTask}
          />
        ) : (
          <TaskList
            key={taskGroupingStore.groupingKey}
            tasks={tasks}
            taskLevel={taskLevel}
            onClearFilters={onClearFilters}
            onSelectTask={onSelectTask}
            defaultValues={defaultValues}
            hideToolbar
          />
        )}
      </>
    );
  },
);

const Right = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.8rem;
`;
